/**
 * R4 批量出片弹框:**一份文案 × N 个形象**(2026-08-08)。
 *
 * ★为什么值得做:这条舱最典型的真实用法是「同一段话,换几个主播各出一条,挑一条发」。
 *   今天要这么做得把整套表单从头填 N 遍 —— 文案、音色、语速、档位每次都重来一遍,
 *   而这些恰恰是**不该变**的那部分。变的只有形象。
 *
 * ★为什么不做「N 个文案 × 1 个形象」:那是 N 条互不相干的片,没有共用的前提,
 *   排成一批只是把 N 次操作叠在一个弹框里,省不掉任何一次思考。
 *
 * ★钱这件事写在按钮上方、按 `预计` 口径(与单条同一份 `estimateDhCost`):
 *   `N 条 × 预计 ≈ X = 预计 ≈ Y 积分`。**不预查余额** —— 租户侧没有余额接口
 *   (实测五个候选路径全 404,唯一带余额的是 TENANT_ADMIN 专属的军师大接口),
 *   与其拿一个猜来的数算「够出几条」,不如把总价说满、让真闸回答(见 dhBatchSubmit 头注)。
 *
 * 跑起来之后**不跳走**:逐条在原地亮起来(排队 → 提交中 → 已提交/失败)。
 *   跳详情页只能跳一个,而这里有 N 个;跑完给一个「去看这批」落到 `/works?line=dh`。
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, ImageIcon, Loader2, Minus } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";
import type { DhBatchItem, DhBatchResult, DhBatchTarget } from "../../model/dhBatchSubmit";

export interface DhBatchDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** 这条线可选的形象(预置 + 我的形象,按线过滤后传进来)。 */
  candidates: DhBatchTarget[];
  /** 单条预计积分(按当前文案/档位算好传进来,与单条出片同一份估算)。 */
  /** 每条预计积分;价目表没到货时为 null —— 不出数字,而不是出 0。 */
  perItemCredits: number | null;
  /** 估算出来的秒数,0 = 还没写文案(此时不报价,不给一个凭空的起步价)。 */
  seconds: number;
  running: boolean;
  items: DhBatchItem[] | null;
  result: DhBatchResult | null;
  onRun: (targets: DhBatchTarget[]) => void;
  onStop: () => void;
  onViewAll: () => void;
}

const STATE_ICON = {
  queued: <Minus className="h-3.5 w-3.5 text-muted-foreground" />,
  submitting: <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground" />,
  done: <Check className="h-3.5 w-3.5 text-success" />,
  failed: <AlertTriangle className="h-3.5 w-3.5 text-destructive" />,
  skipped: <Minus className="h-3.5 w-3.5 text-muted-foreground" />,
};

const STATE_TEXT = {
  queued: "排队中",
  submitting: "提交中…",
  done: "已提交",
  failed: "失败",
  skipped: "未提交",
};

export function DhBatchDialog(props: DhBatchDialogProps) {
  const {
    open,
    onOpenChange,
    candidates,
    perItemCredits,
    seconds,
    running,
    items,
    result,
    onRun,
    onStop,
    onViewAll,
  } = props;
  const [picked, setPicked] = useState<string[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  // 每次重新打开都从空选开始:上一批选了谁与这一批无关,留着只会误提交。
  useEffect(() => {
    if (open && !running && !result) setPicked([]);
  }, [open, running, result]);

  const toggle = (k: string) =>
    setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const chosen = candidates.filter((c) => picked.includes(c.key));
  const total = perItemCredits == null ? null : perItemCredits * chosen.length;

  return (
    <Dialog open={open} onOpenChange={(v) => (running ? undefined : onOpenChange(v))}>
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-3 sm:max-w-2xl"
        data-testid="dh-batch-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base">批量出片 · 一份文案 × 多个形象</DialogTitle>
          <DialogDescription className="text-xs">
            文案、音色、语速、档位全部沿用当前这一份,只换形象。选中几位就出几条。
          </DialogDescription>
        </DialogHeader>

        {items ? (
          // ── 跑起来之后:逐条在原地亮 ──────────────────────────────────────
          <div ref={listRef} className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
            {items.map((it) => (
              <div
                key={it.target.key}
                data-testid={`dh-batch-item-${it.target.key}`}
                data-state={it.state}
                className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs"
              >
                {STATE_ICON[it.state]}
                <span className="min-w-0 flex-1 truncate font-medium">{it.target.label}</span>
                <span
                  className={cn(
                    "shrink-0",
                    it.state === "failed" ? "text-destructive" : "text-muted-foreground"
                  )}
                >
                  {STATE_TEXT[it.state]}
                </span>
                {/* ★失败原因逐条显示:一条失败不否决其余,那这条失败的理由也只属于它自己 */}
                {it.error && (
                  <span className="max-w-[45%] shrink-0 truncate text-destructive" title={it.error}>
                    {it.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          // ── 选形象 ────────────────────────────────────────────────────
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {candidates.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted-foreground">
                这条线还没有可批量的形象。先在「选形象」里挑一位,或去人物库保存一个。
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {candidates.map((c) => {
                  const on = picked.includes(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      aria-pressed={on}
                      aria-label={`选择 ${c.label}`}
                      data-testid={`dh-batch-pick-${c.key}`}
                      onClick={() => toggle(c.key)}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-md border p-1.5 transition hover:bg-muted",
                        on && "border-primary ring-2 ring-primary"
                      )}
                    >
                      {c.thumbnailUrl ? (
                        c.kind === "video" ? (
                          // video 档的 thumbnailUrl 是源视频 presign 地址 —— <img> 塞 mp4
                          // 必裂(0901「批量出分身预览图拿不到」),与单选面板同款取首帧。
                          <video
                            src={`${c.thumbnailUrl}#t=0.1`}
                            muted
                            playsInline
                            preload="metadata"
                            className="aspect-square w-full rounded bg-black object-cover"
                          />
                        ) : (
                          <img
                            src={c.thumbnailUrl}
                            alt={c.label}
                            loading="lazy"
                            className="aspect-square w-full rounded object-cover"
                          />
                        )
                      ) : (
                        <div className="flex aspect-square w-full items-center justify-center rounded bg-muted">
                          <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <span className="w-full truncate text-2xs text-muted-foreground">
                        {c.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 钱与结果都焊在底部:掏钱的那一下手指就在这儿 */}
        <div className="shrink-0 space-y-2 border-t pt-2">
          {result ? (
            <p className="text-xs" data-testid="dh-batch-summary">
              <span className="font-medium text-foreground">
                成功 {result.succeeded} 条{result.failed > 0 && ` · 失败 ${result.failed} 条`}
                {result.skipped > 0 && ` · 未提交 ${result.skipped} 条`}
              </span>
              {/* ★收手要说清「后面那些根本没提交」,别让用户以为它们也扣了钱 */}
              {result.stoppedForBalance && (
                <span className="text-destructive">
                  {" "}
                  —— 余额/配额不足,剩下 {result.skipped} 条没有提交,也没有扣费。
                </span>
              )}
              {result.aborted && <span className="text-muted-foreground"> —— 你停下了。</span>}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="dh-batch-cost">
              {seconds > 0 && chosen.length > 0
                ? perItemCredits == null || total == null
                  ? `${chosen.length} 条(按当前文案 ≈ ${seconds} 秒;以实扣为准)`
                  : `${chosen.length} 条 × 预计 ≈ ${perItemCredits.toLocaleString()} 积分 = 预计 ≈ ${total.toLocaleString()} 积分(按当前文案 ≈ ${seconds} 秒;以实扣为准)`
                : seconds > 0
                  ? "选中几位形象,这里会把总预计算给你看"
                  : "先写文案 —— 分身线按秒计费,没有文案算不出预计"}
            </p>
          )}

          <DialogFooter className="gap-2">
            {running ? (
              <Button type="button" variant="outline" onClick={onStop} data-testid="dh-batch-stop">
                停下(已提交的不会撤)
              </Button>
            ) : result ? (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  关闭
                </Button>
                <Button type="button" onClick={onViewAll} data-testid="dh-batch-viewall">
                  去看这批
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  取消
                </Button>
                <Button
                  type="button"
                  disabled={chosen.length === 0 || seconds === 0}
                  onClick={() => onRun(chosen)}
                  data-testid="dh-batch-run"
                >
                  {chosen.length === 0
                    ? "先选形象"
                    : seconds === 0
                      ? "先写文案"
                      : `出 ${chosen.length} 条`}
                </Button>
              </>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
